import { request } from '@synergy/api';
import { useSyncExternalStore } from 'react';

/**
 * The narrowing part of a query — the part a list can refuse.
 *
 * Sorted, so that the same filter asked twice is recognised as the same one
 * however the parameters happened to be ordered.
 */
export const paloFilterPart = (search: string): string => {
    const held = Array.from(new URLSearchParams(search).entries())
        .filter(([key]) => key.startsWith('filter['))
        .map(([key, value]) => `${key}=${value}`);

    held.sort();

    return held.join('&');
};

/** The filter a list last refused to answer, as `paloFilterPart` writes it. */
let refused: string | null = null;
const listeners = new Set<() => void>();

const announce = (next: string | null): void => {
    if (next === refused) {
        return;
    }

    refused = next;
    listeners.forEach(listener => listener());
};

const subscribe = (listener: () => void): (() => void) => {
    listeners.add(listener);

    return () => {
        listeners.delete(listener);
    };
};

/** What the list last refused, for the screens that must not remember it. */
export const paloRefusedFilter = (): string | null => refused;

export const usePaloRefusedFilter = (): string | null =>
    useSyncExternalStore(
        subscribe,
        () => refused,
        () => null
    );

/**
 * Whether a query carries exactly the filter that was refused.
 *
 * Also the answer to whether the list has already said why it came back
 * empty: a refused filter is written into the list's own body, beside the
 * chips that caused it and with a way to undo it. The toast that went up as
 * well said the same thing a second time, in words that name no cause and
 * offer no way out — so the screen told it twice and helped once (#2231
 * R-118).
 */
export const paloIsRefused = (search: string): boolean => null !== refused && paloFilterPart(search) === refused;

/**
 * Watches what the lists ask for.
 *
 * A filter the list cannot answer used to leave the rows it had before
 * standing, under chips that said the filter was applied — so the screen
 * showed an unnarrowed list as if it were the answer, and the filter was
 * remembered and asked again on the next visit (#2231 R-118). The refusal is
 * noted here, against the filter that caused it, and the screens read it from
 * one place rather than each keeping an error of its own.
 *
 * Only a query that narrows something is watched: a request without a filter
 * can be refused for a hundred reasons that are not the list's filter.
 */
request.interceptors.response.use(
    response => {
        const search = queryOf(response?.config);

        if (null !== search && paloFilterPart(search) === refused) {
            announce(null);
        }

        return response;
    },
    error => {
        const status = error?.response?.status;
        const search = queryOf(error?.config);

        // Signed out is not a filter that cannot be answered, and neither is a
        // request that was dropped because the next one overtook it.
        if (null !== search && status >= 400 && 401 !== status && 403 !== status && 'ERR_CANCELED' !== error?.code) {
            announce(paloFilterPart(search));
        }

        throw error;
    }
);

/**
 * The query a request narrows with, or nothing.
 *
 * Taken from wherever it was given: the lists hand their filters to axios as
 * `params` rather than writing them into the address they call, so reading
 * the url alone found no filter on any of them.
 */
function queryOf(config?: { url?: string; params?: unknown }): string | null {
    const at = config?.url?.indexOf('?') ?? -1;
    const inUrl = at >= 0 ? (config?.url as string).substring(at + 1) : '';
    const given = config?.params;
    const inParams =
        given instanceof URLSearchParams
            ? given.toString()
            : given && 'object' === typeof given
            ? new URLSearchParams(given as Record<string, string>).toString()
            : '';
    const search = [inUrl, inParams].filter(part => '' !== part).join('&');

    return '' === paloFilterPart(search) ? null : search;
}
